// frontend/src/components/layout/Tile.jsx - Flexible tile/card wrapper component

import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ICON } from '../../icons';
import DisclosureCaret from '../common/DisclosureCaret';
import { IconButton } from '@mui/material';
import InfoHint from '../common/InfoHint';
import { useAuth } from '../../contexts/AuthContext';
import './Tile.css';

const Tile = ({
  width = 'full',
  title,
  titleState,
  subtitle,
  infoText,
  subtitleAutoHide = false,
  toolbar,
  editActions,
  headerActions,
  collapsedSummary,
  collapsedItems,
  children,
  className = '',
  collapsible = false,
  storageKey,
}) => {
  const { t } = useTranslation();
  const { userContext, updatePreference } = useAuth();

  const AUTOHIDE_DAYS = 60;

  const { resolvedSubtitle, resolvedInfoText } = useMemo(() => {
    if (!subtitleAutoHide || !subtitle) {
      return { resolvedSubtitle: subtitle, resolvedInfoText: infoText };
    }
    const joined = userContext?.user?.date_joined;
    if (!joined) {
      return { resolvedSubtitle: subtitle, resolvedInfoText: infoText };
    }
    // Date.now() inside useMemo body — purity rule is conservative;
    // useMemo's deps cache the result per (joined) cycle. The day
    // boundary only matters at the AUTOHIDE_DAYS threshold and joined
    // is stable per user, so re-running Date.now() across renders
    // produces equivalent results.
    // eslint-disable-next-line react-hooks/purity
    const daysSinceJoin = (Date.now() - new Date(joined).getTime()) / (1000 * 60 * 60 * 24);
    if (daysSinceJoin > AUTOHIDE_DAYS) {
      return { resolvedSubtitle: null, resolvedInfoText: infoText || subtitle };
    }
    return { resolvedSubtitle: subtitle, resolvedInfoText: infoText };
  }, [subtitleAutoHide, subtitle, infoText, userContext?.user?.date_joined]);

  const [collapsed, setCollapsed] = useState(() => {
    if (!collapsible || !storageKey) return false;
    return userContext?.user?.ui_preferences?.collapsed_tiles?.[storageKey] ?? false;
  });

  const toggleCollapsed = useCallback(() => {
    setCollapsed(prev => {
      const next = !prev;
      if (storageKey) {
        updatePreference('collapsed_tiles', (existing) => ({
          ...(existing || {}),
          [storageKey]: next,
        }));
      }
      return next;
    });
  }, [storageKey, updatePreference]);

  const tileClass = `tile tile-${width} ${className}`;

  const body = (
    <>
      {toolbar && (
        <div className="tile-toolbar">
          {toolbar}
        </div>
      )}
      <div className="tile-content">
        {children}
      </div>
    </>
  );

  return (
    <div className={tileClass}>
      {title && (
        <div
          className={`tile-header ${collapsible ? 'tile-header-collapsible' : ''}`}
          onClick={collapsible ? toggleCollapsed : undefined}
        >
          <div className="tile-title-block">
            <div className="tile-title-row">
              {/* One inline run, so the icon follows the title's last word
                  when the title wraps. */}
              <h3 className="tile-title">
                <span>
                  {title}
                  {resolvedInfoText && (
                    <InfoHint text={resolvedInfoText} className="tile-info-icon" />
                  )}
                </span>
              </h3>
              {/* State that changes what the tile shows (a scope chip with
                  its own clear button): on the title line, so it never adds
                  a line to the header, and outside the heading, so its
                  button is not part of the title. */}
              {titleState}
            </div>
            {resolvedSubtitle && <p className="tile-subtitle">{resolvedSubtitle}</p>}
          </div>
          {collapsed && (collapsedItems || collapsedSummary) && (
            <div className="tile-collapsed-summary">
              {collapsedItems
                ? collapsedItems.map((item, i) => (
                    <span key={i} className="tile-collapsed-summary-item">
                      <span className="tile-collapsed-summary-label">{item.label}:</span>{' '}
                      <span className="tile-collapsed-summary-value">{item.value}</span>
                    </span>
                  ))
                : collapsedSummary
              }
            </div>
          )}
          {/* The three action slots:
              `toolbar` — the row under the title: filters and view toggles,
                then the tile's one main action, the row's only filled
                button. A tile with nothing to filter has no toolbar: its
                main action leads `headerActions` instead of standing alone
                on a row.
              `editActions` — top right, first: Save / Discard of pending
                edits, a group of their own.
              `headerActions` — top right, after them: the main action of a
                tile with no toolbar, then outlined Recalculate, settings,
                Bulk update / Import, and Export always last.
              Status that belongs to the heading (a status chip, a spinner)
              goes inside `title`. */}
          <div className="tile-header-right">
            {editActions && (
              <div className="tile-edit-actions">
                {editActions}
              </div>
            )}
            {headerActions && (
              <div className="tile-header-actions">
                {headerActions}
              </div>
            )}
            {collapsible && (
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); toggleCollapsed(); }}
                className="tile-collapse-btn"
                aria-label={collapsed ? t('btn.expand') : t('btn.collapse')}
              >
                <DisclosureCaret open={!collapsed} size={ICON.xl} />
              </IconButton>
            )}
          </div>
        </div>
      )}
      {collapsible ? (
        <div className={`tile-collapsible-wrapper ${collapsed ? 'tile-collapsed' : ''}`}>
          <div className="tile-collapsible-inner">
            {body}
          </div>
        </div>
      ) : body}
    </div>
  );
};

export default Tile;
